import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Heart, Minus, Plus, Star, ShoppingBag, Sparkles, Crown, Leaf, Gift,
  ChevronRight, Check, Clock, Truck, ShieldCheck,
} from "lucide-react";
import { formatINR } from "@/lib/api";
import { getTheme } from "@/lib/themes";
import Reveal from "@/components/Reveal";
import FrequentlyBoughtTogether from "@/components/FrequentlyBoughtTogether";
import DesignVariants from "@/components/DesignVariants";

const ICONS = { Sparkles, Crown, Leaf, Gift };

// Floating lantern — repeats with random delay & drift
const Lantern = ({ delay = 0, left = "20%", size = 36, duration = 14, opacity = 0.9 }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{ left, bottom: "-10%", width: size, height: size * 1.4 }}
    initial={{ y: 0, opacity: 0 }}
    animate={{
      y: [0, -800, -1600],
      x: [0, 10, -10, 0],
      opacity: [0, opacity, opacity, 0],
    }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeOut" }}
  >
    <div className="relative w-full h-full">
      <div
        className="absolute inset-0 rounded-[40%_40%_45%_45%/55%_55%_50%_50%]"
        style={{
          background: "radial-gradient(circle at 50% 60%, #FFE082 0%, #F7C34A 45%, rgba(247,195,74,0) 80%)",
          boxShadow: "0 0 32px 8px rgba(247,208,112,0.55)",
        }}
      />
      <div className="absolute top-[-8px] left-1/2 -translate-x-1/2 w-[1px] h-3 bg-amber-900/40" />
      <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-[2px] h-2 bg-amber-900/40" />
    </div>
  </motion.div>
);

// Sparkle dot used around hero
const Sparkle = ({ top, left, delay = 0, size = 10 }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{ top, left, width: size, height: size }}
    animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4], rotate: [0, 180, 360] }}
    transition={{ duration: 2.8, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    <Sparkles size={size} className="text-[#F7D070]" fill="#F7D070" />
  </motion.div>
);

const ThemedProductDetail = ({ product, onAddToCart, onAddToWishlist }) => {
  const theme = getTheme(product.theme_slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const backdropY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const [mainIdx, setMainIdx] = useState(0);
  const [size, setSize] = useState((product.sizes || [])[0] || null);
  const [qty, setQty] = useState(1);

  // Auto-cycle product gallery
  useEffect(() => {
    if (!product.images || product.images.length < 2) return;
    const id = setInterval(() => setMainIdx((i) => (i + 1) % product.images.length), 4200);
    return () => clearInterval(id);
  }, [product.images]);

  useEffect(() => { window.scrollTo(0, 0); }, [product.slug]);

  if (!theme) return null;
  const p = theme.palette;

  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  return (
    <div
      className="themed-pdp font-body relative"
      style={{ background: p.bg, color: p.ink }}
      data-testid="themed-pdp"
      data-theme={theme.slug}
    >
      {/* Breadcrumb — themed */}
      <div className="rieave-container pt-8 pb-2 relative z-10">
        <nav className="flex items-center gap-1.5 text-xs" style={{ color: `${p.ink}99` }}>
          <Link to="/" className="hover:underline">Home</Link>
          <ChevronRight size={12} />
          <Link to={`/shop/characters/${theme.slug}`} className="hover:underline capitalize">{theme.name} edit</Link>
          <ChevronRight size={12} />
          <span style={{ color: p.ink }}>{product.name}</span>
        </nav>
      </div>

      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        className="relative min-h-[92vh] overflow-hidden"
        data-testid="themed-hero"
      >
        {/* Parallax backdrop */}
        <motion.div
          className="absolute inset-0"
          style={{ y: backdropY, scale: heroScale }}
        >
          <img src={theme.backdrop_image} alt="" className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(180deg, ${p.bg}cc 0%, transparent 30%, transparent 60%, ${p.bg}f5 100%),
                           radial-gradient(ellipse at 70% 30%, rgba(247,208,112,0.35), transparent 55%),
                           radial-gradient(ellipse at 20% 80%, rgba(139,90,124,0.25), transparent 60%)`,
            }}
          />
        </motion.div>

        {/* Floating lanterns */}
        <Lantern delay={0} left="8%"  size={28} duration={18} opacity={0.6} />
        <Lantern delay={3} left="18%" size={40} duration={22} opacity={0.85} />
        <Lantern delay={6} left="74%" size={32} duration={20} opacity={0.75} />
        <Lantern delay={9} left="88%" size={44} duration={24} opacity={0.9} />
        <Lantern delay={12} left="55%" size={28} duration={20} opacity={0.55} />

        {/* Sparkles */}
        <Sparkle top="18%" left="15%" delay={0.2} size={14} />
        <Sparkle top="32%" left="82%" delay={1.1} size={18} />
        <Sparkle top="60%" left="10%" delay={1.8} size={10} />
        <Sparkle top="72%" left="88%" delay={2.4} size={16} />

        <div className="relative rieave-container pt-14 md:pt-20 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — product gallery with character collage */}
          <div className="lg:col-span-7 relative">
            {/* Character portrait — decorative floating badge */}
            <motion.div
              className="hidden md:block absolute -top-4 -left-6 w-[170px] h-[220px] rounded-[2px] overflow-hidden border-[6px] border-white shadow-2xl z-20 rotate-[-4deg]"
              initial={{ opacity: 0, y: 20, rotate: -12 }}
              animate={{ opacity: 1, y: 0, rotate: -4 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <img src={theme.hero_image} alt={theme.name} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 text-center py-1.5 text-[10px] uppercase tracking-[0.26em] text-white" style={{ background: `linear-gradient(180deg, transparent, ${p.ink}cc)` }}>
                {theme.name}
              </div>
            </motion.div>

            {/* Main product image — golden vignette */}
            <motion.div
              className="relative aspect-[4/5] max-w-[520px] mx-auto rounded-[2px] overflow-hidden"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              style={{ boxShadow: `0 40px 80px -20px ${p.ink}40, 0 0 0 1px ${p.gold}40` }}
              data-testid="themed-hero-image"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={mainIdx}
                  src={product.images[mainIdx]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
              </AnimatePresence>
              {/* Golden glow overlay */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-soft-light"
                style={{ background: `radial-gradient(ellipse at 50% 100%, ${p.glow}80, transparent 60%)` }}
              />
            </motion.div>

            {/* Thumbnails row */}
            <div className="flex items-center justify-center gap-3 mt-6" data-testid="themed-thumbs">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setMainIdx(i)}
                  className="relative w-16 h-20 overflow-hidden rounded-[2px] transition-all"
                  style={{
                    borderWidth: 2,
                    borderStyle: "solid",
                    borderColor: mainIdx === i ? p.gold : "transparent",
                    boxShadow: mainIdx === i ? `0 0 0 3px ${p.glow}40` : "none",
                  }}
                  data-testid={`themed-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT — buy panel */}
          <motion.div
            className="lg:col-span-5 relative z-10"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            <div
              className="p-7 md:p-9 rounded-[2px] backdrop-blur-sm"
              style={{ background: `${p.surface}ee`, border: `1px solid ${p.gold}40` }}
            >
              <motion.p
                className="text-[11px] tracking-[0.32em] uppercase mb-4 font-medium"
                style={{ color: p.gold }}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              >
                ✦ {theme.tagline} ✦
              </motion.p>

              <motion.h1
                className="font-hero text-4xl md:text-5xl leading-[1] mb-4"
                style={{ color: p.ink }}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                data-testid="themed-product-name"
              >
                {product.name}
              </motion.h1>

              <div className="flex items-center gap-2 mb-5">
                <div className="flex items-center gap-0.5" style={{ color: p.gold }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <span className="text-xs" style={{ color: `${p.ink}99` }}>{product.rating} · {product.review_count} reviews</span>
              </div>

              <p className="mb-6 text-[15px] leading-[1.65]" style={{ color: `${p.ink}cc` }}>
                {product.short_description}
              </p>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-hero text-4xl" style={{ color: p.ink }} data-testid="themed-product-price">{formatINR(product.price)}</span>
                {product.compare_at_price > product.price && (
                  <>
                    <span className="text-lg line-through" style={{ color: `${p.ink}60` }}>{formatINR(product.compare_at_price)}</span>
                    <span className="px-3 py-1 text-[10px] tracking-[0.2em] uppercase rounded-full font-medium" style={{ background: p.glow, color: p.ink }}>
                      Save {discount}%
                    </span>
                  </>
                )}
              </div>

              {/* Size */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-5">
                  <p className="text-[11px] tracking-[0.28em] uppercase mb-3" style={{ color: `${p.ink}99` }}>Chain length</p>
                  <div className="flex gap-2 flex-wrap">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        className="px-4 py-2 rounded-full text-sm transition-all"
                        style={{
                          border: `1.5px solid ${size === s ? p.gold : `${p.ink}30`}`,
                          background: size === s ? p.gold : "transparent",
                          color: size === s ? "#fff" : p.ink,
                        }}
                        data-testid={`themed-size-${s.replace(/\s+/g, "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Qty + stock */}
              <div className="flex items-center gap-4 mb-6">
                <p className="text-[11px] tracking-[0.28em] uppercase" style={{ color: `${p.ink}99` }}>Qty</p>
                <div className="flex items-center rounded-full" style={{ border: `1.5px solid ${p.ink}20` }}>
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center" aria-label="minus"><Minus size={12} /></button>
                  <span className="w-9 text-center text-sm">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="w-9 h-9 flex items-center justify-center" aria-label="plus"><Plus size={12} /></button>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "#2F7A4A" }}>
                  <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2 }}>
                    <Check size={14} />
                  </motion.span>
                  {product.stock > 5 ? "In stock · ships today" : `Only ${product.stock} left`}
                </span>
              </div>

              {/* CTAs */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => onAddToCart(size, qty)}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-4 rounded-full text-[12px] tracking-[0.24em] uppercase font-semibold text-white shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${p.gold} 0%, ${p.glow} 50%, ${p.gold} 100%)`,
                    boxShadow: `0 10px 30px -10px ${p.gold}cc`,
                  }}
                  data-testid="themed-add-to-cart"
                >
                  <ShoppingBag size={14} /> Add to bag
                </button>
                <button
                  onClick={onAddToWishlist}
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ border: `1.5px solid ${p.ink}30` }}
                  aria-label="wishlist"
                  data-testid="themed-wishlist"
                >
                  <Heart size={16} style={{ color: p.ink }} />
                </button>
              </div>

              {/* Trust row */}
              <div className="grid grid-cols-3 gap-3 pt-5" style={{ borderTop: `1px solid ${p.ink}15` }}>
                {[
                  { icon: Truck, t: "Free ship", s: "on orders ₹1499+" },
                  { icon: ShieldCheck, t: "Lifetime", s: "polishing promise" },
                  { icon: Clock, t: "Ships in", s: "24 hours" },
                ].map((b, i) => {
                  const Ic = b.icon;
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <Ic size={16} style={{ color: p.gold }} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium leading-tight" style={{ color: p.ink }}>{b.t}</p>
                        <p className="text-[11px]" style={{ color: `${p.ink}80` }}>{b.s}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ STORY NARRATIVE ============ */}
      <section className="relative overflow-hidden py-24 md:py-32" style={{ background: p.surface }} data-testid="themed-story">
        <div className="rieave-container grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <Reveal>
              <div className="relative">
                <img src={theme.gallery[1]?.src} alt="" className="w-full aspect-[4/5] object-cover rounded-[2px]" style={{ boxShadow: `0 30px 60px -20px ${p.ink}50` }} />
                {/* corner flower ornament */}
                <motion.div
                  className="absolute -bottom-10 -right-6 w-[40%] aspect-square rounded-full overflow-hidden border-[6px] border-white shadow-2xl"
                  initial={{ rotate: 10, opacity: 0, y: 30 }}
                  whileInView={{ rotate: 0, opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <img src={theme.flower_image} alt="" className="w-full h-full object-cover" />
                </motion.div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal>
              <p className="text-[11px] tracking-[0.32em] uppercase mb-4 font-medium" style={{ color: p.gold }}>
                {theme.story.eyebrow}
              </p>
              <h2 className="font-hero text-4xl md:text-5xl leading-[1.1] mb-6" style={{ color: p.ink }}>
                {theme.story.title}
              </h2>
              <p className="text-[17px] leading-[1.75] mb-8" style={{ color: `${p.ink}bb` }}>
                {theme.story.body}
              </p>
              <blockquote
                className="relative pl-6 italic font-hero text-2xl md:text-3xl"
                style={{ color: p.gold, borderLeft: `2px solid ${p.gold}` }}
              >
                {theme.story.quote}
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ VALUE PROPS ============ */}
      <section className="py-24 md:py-28 relative" style={{ background: p.bg }} data-testid="themed-values">
        <div className="rieave-container">
          <Reveal>
            <div className="text-center max-w-xl mx-auto mb-14">
              <p className="text-[11px] tracking-[0.32em] uppercase mb-3" style={{ color: p.gold }}>Why you'll love it</p>
              <h2 className="font-hero text-4xl md:text-5xl" style={{ color: p.ink }}>Crafted with a little magic.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {theme.value_props.map((v, i) => {
              const Ic = ICONS[v.icon] || Sparkles;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    className="p-7 h-full rounded-[2px]"
                    style={{ background: p.surface, border: `1px solid ${p.gold}30` }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                      style={{ background: `${p.glow}55` }}
                    >
                      <Ic size={20} style={{ color: p.gold }} />
                    </div>
                    <h3 className="font-hero text-xl mb-2" style={{ color: p.ink }}>{v.title}</h3>
                    <p className="text-sm leading-[1.6]" style={{ color: `${p.ink}99` }}>{v.body}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CHARACTER GALLERY — Complete the look ============ */}
      <section className="py-24 md:py-32" style={{ background: p.surface }} data-testid="themed-gallery">
        <div className="rieave-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-[11px] tracking-[0.32em] uppercase mb-3" style={{ color: p.gold }}>From the story</p>
              <h2 className="font-hero text-4xl md:text-5xl" style={{ color: p.ink }}>
                Moments of <span className="italic" style={{ color: p.gold }}>golden</span> magic.
              </h2>
            </div>
            <Link
              to={`/shop/characters/${theme.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-[0.24em] transition-all"
              style={{ border: `1.5px solid ${p.ink}`, color: p.ink }}
              data-testid="themed-see-edit"
            >
              See the full {theme.name} edit <ChevronRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {theme.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="group relative aspect-[3/4] overflow-hidden rounded-[2px]"
                  style={{ boxShadow: `0 20px 40px -20px ${p.ink}40` }}
                >
                  <motion.img
                    src={g.src} alt={g.caption}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.8 }}
                  />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                       style={{ background: `linear-gradient(180deg, transparent 40%, ${p.ink}dd)` }} />
                  <motion.p
                    className="absolute bottom-4 left-4 right-4 font-hero text-lg text-white translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                  >
                    {g.caption}
                  </motion.p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ DESIGN VARIANTS ============ */}
      <DesignVariants slug={product.slug} variant="themed" />

      {/* ============ FREQUENTLY BOUGHT TOGETHER ============ */}
      <FrequentlyBoughtTogether slug={product.slug} />

      {/* ============ CTA STRIP ============ */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        data-testid="themed-cta"
      >
        <div className="absolute inset-0">
          <img src={theme.cta_background} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${p.ink}dd 0%, ${p.ink}99 50%, ${p.accent}aa 100%)` }} />
        </div>

        {/* Lanterns in CTA too */}
        <Lantern delay={0} left="12%" size={32} duration={18} opacity={0.7} />
        <Lantern delay={4} left="80%" size={40} duration={22} opacity={0.9} />

        <div className="relative rieave-container text-center text-white max-w-3xl mx-auto">
          <Reveal>
            <Sparkles size={28} className="mx-auto mb-5" style={{ color: p.glow }} />
            <h2 className="font-hero text-5xl md:text-7xl leading-[1.02] mb-5">
              Write your <span className="italic" style={{ color: p.glow }}>own</span> fairytale.
            </h2>
            <p className="text-lg md:text-xl mb-10 text-white/80">
              Limited-edition piece from the {theme.name} Edit. Once the lanterns go out, they're gone.
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                onAddToCart(size, qty);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full text-sm uppercase tracking-[0.26em] font-semibold text-ink"
              style={{
                background: `linear-gradient(135deg, ${p.gold}, ${p.glow}, ${p.gold})`,
                color: p.ink,
                boxShadow: `0 20px 60px -10px ${p.gold}cc`,
              }}
              data-testid="themed-cta-button"
            >
              <ShoppingBag size={15} /> Claim your piece · {formatINR(product.price)}
            </motion.button>
          </Reveal>
        </div>
      </section>

      {/* Mobile sticky buy bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-30 md:hidden p-3"
        style={{ background: `${p.surface}f5`, borderTop: `1px solid ${p.gold}40`, backdropFilter: "blur(12px)" }}
      >
        <button
          onClick={() => onAddToCart(size, qty)}
          className="w-full inline-flex items-center justify-between px-5 py-3.5 rounded-full text-white font-semibold"
          style={{ background: `linear-gradient(135deg, ${p.gold}, ${p.glow}, ${p.gold})` }}
          data-testid="themed-mobile-add"
        >
          <span className="uppercase tracking-[0.22em] text-[12px]">Add to bag</span>
          <span className="font-hero text-lg">{formatINR(product.price)}</span>
        </button>
      </div>
    </div>
  );
};

export default ThemedProductDetail;
